/**
 * Phase 5: Document Panel
 * Shows generated documents for a submitted workflow and allows users to:
 *  - View & download each document (plain-text for now)
 *  - Trigger regeneration
 *  - Request attorney review
 * Also shows the attorney review status and comments if a review was requested.
 */

import React, { useState, useEffect, useCallback } from 'react';
import FormExportPanel from './FormExportPanel';
import CaseStatusPanel from './CaseStatusPanel';
import EvidenceBuilderPanel from './EvidenceBuilderPanel';
import PriorityDatePanel from './PriorityDatePanel';
import { LAUNCH } from '../lib/launch';
import { BetaBadge, PurchaseDisabledButton } from './ComingSoon';
import { API_BASE as API } from '../lib/apiBase';

// Employment-based pathways that have Priority Date Intelligence
const EB_PATHWAYS = new Set(['eb2-niw', 'eb1a', 'perm-employer', 'o1-nonimmigrant']);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.detail?.message || data?.detail || 'Request failed';
    throw Object.assign(new Error(msg), { status: res.status, data });
  }
  return data;
};

const DOC_ICONS = {
  petition_pdf: '📕',
  petition_docx: '📘',
  petition_draft: '📄',
  filing_checklist: '✅',
  cover_letter: '📬',
  evidence_summary: '🗂️',
  narrative: '✍️',
};

const REVIEW_STATUS_COLORS = {
  pending_payment: 'text-yellow-600 bg-yellow-50',
  active: 'text-blue-600 bg-blue-50',
  in_review: 'text-purple-600 bg-purple-50',
  completed: 'text-green-600 bg-green-50',
};

const REVIEW_STATUS_LABELS = {
  pending_payment: '⏳ Pending Payment',
  active: '📋 Awaiting Attorney',
  in_review: '🔍 Under Review',
  completed: '✅ Review Complete',
};

/**
 * Trigger a binary file download with auth header.
 */
const downloadBinary = async (path, fallbackFilename) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.detail?.message || data?.detail || 'Download failed');
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Document Viewer Modal (text + narrative edit)
// ---------------------------------------------------------------------------
const DocumentViewer = ({ doc, pathwayId, onClose, onNarrativeUpdated }) => {
  const [editContent, setEditContent] = useState(doc?.content || '');
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');

  if (!doc) return null;

  const isNarrative = doc.document_type === 'narrative';
  const isFinalized = doc.narrative_status === 'finalized';
  const canEditNarrative = isNarrative && !isFinalized;
  const hasPdf = doc.document_type === 'petition_pdf' || doc.has_binary;

  const handleDownloadTxt = () => {
    const blob = new Blob([doc.content || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.document_type}_${doc.pathway_id}_v${doc.version}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    setDownloading('pdf');
    setLocalError('');
    try {
      await downloadBinary(
        `/api/documents/${pathwayId}/${doc.id}/download/pdf`,
        `${doc.document_type}_${pathwayId}.pdf`
      );
    } catch (err) {
      setLocalError(err.message || 'PDF download failed');
    } finally {
      setDownloading('');
    }
  };

  const handleDownloadDocx = async () => {
    setDownloading('docx');
    setLocalError('');
    try {
      await downloadBinary(
        `/api/documents/${pathwayId}/${doc.id}/download/docx`,
        `${doc.document_type}_${pathwayId}.docx`
      );
    } catch (err) {
      setLocalError(err.message || 'DOCX download failed');
    } finally {
      setDownloading('');
    }
  };

  const handleSaveNarrative = async () => {
    setSaving(true);
    setLocalError('');
    setLocalSuccess('');
    try {
      const data = await apiFetch(`/api/documents/${pathwayId}/${doc.id}/narrative`, {
        method: 'PUT',
        body: JSON.stringify({ content: editContent }),
      });
      setLocalSuccess('Narrative draft saved');
      if (onNarrativeUpdated && data.document) {
        onNarrativeUpdated(data.document);
      }
    } catch (err) {
      setLocalError(err.message || 'Failed to save narrative');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizeNarrative = async () => {
    setFinalizing(true);
    setLocalError('');
    setLocalSuccess('');
    try {
      // Save latest edits first if draft
      if (canEditNarrative && editContent !== doc.content) {
        await apiFetch(`/api/documents/${pathwayId}/${doc.id}/narrative`, {
          method: 'PUT',
          body: JSON.stringify({ content: editContent }),
        });
      }
      const data = await apiFetch(
        `/api/documents/${pathwayId}/${doc.id}/narrative/finalize`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      setLocalSuccess('Narrative finalized');
      if (onNarrativeUpdated && data.document) {
        onNarrativeUpdated(data.document);
      }
    } catch (err) {
      setLocalError(err.message || 'Failed to finalize narrative');
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {DOC_ICONS[doc.document_type] || '📄'} {doc.document_type_label}
              {isNarrative && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-semibold ${
                  isFinalized ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {isFinalized ? 'Finalized' : 'Draft'}
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Generated: {doc.generated_at ? new Date(doc.generated_at).toLocaleDateString() : '—'}
              {' · '}
              v{doc.version}
              {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(1)} KB` : ''}
              {doc.narrative_type_label ? ` · ${doc.narrative_type_label}` : ''}
              {doc.form_number ? ` · Form ${doc.form_number}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {hasPdf && (
              <button
                onClick={handleDownloadPdf}
                disabled={!!downloading}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-sm disabled:opacity-50"
              >
                <i className="fas fa-file-pdf"></i>
                {downloading === 'pdf' ? '…' : 'PDF'}
              </button>
            )}
            <button
              onClick={handleDownloadDocx}
              disabled={!!downloading}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm disabled:opacity-50"
            >
              <i className="fas fa-file-word"></i>
              {downloading === 'docx' ? '…' : 'DOCX'}
            </button>
            {!hasPdf && (
              <button
                onClick={handleDownloadTxt}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-semibold text-sm"
              >
                <i className="fas fa-download"></i> Text
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors ml-1"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {localError && (
            <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">
              {localError}
            </div>
          )}
          {localSuccess && (
            <div className="mb-3 bg-green-50 border border-green-200 text-green-700 text-sm p-3 rounded-lg">
              {localSuccess}
            </div>
          )}

          {canEditNarrative ? (
            <div className="space-y-3">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full min-h-[420px] font-mono text-sm text-gray-800 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveNarrative}
                  disabled={saving || finalizing}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg font-semibold text-sm hover:bg-yellow-600 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save Draft'}
                </button>
                <button
                  onClick={handleFinalizeNarrative}
                  disabled={saving || finalizing}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  {finalizing ? 'Finalizing…' : 'Finalize'}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Drafts are editable. Finalizing locks the narrative for filing review.
              </p>
            </div>
          ) : doc.content ? (
            <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-200">
              {doc.content}
            </pre>
          ) : hasPdf ? (
            <div className="text-center text-gray-500 py-8">
              <i className="fas fa-file-pdf text-4xl mb-3 block text-red-400"></i>
              <p className="font-medium text-gray-700">Filled petition PDF ready</p>
              <p className="text-sm mt-2">Use the PDF or DOCX buttons above to download.</p>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <i className="fas fa-file-alt text-4xl mb-3 block"></i>
              <p>Content not available. Click download to get the document.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <p className="text-xs text-gray-400 text-center">
            ⚠️ This is a draft document for review purposes only. Consult with a qualified attorney before filing.
          </p>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Attorney Review Panel — Phase 11
// Purchase-gated: must buy at checkout or as standalone add-on.
// ---------------------------------------------------------------------------
const AttorneyReviewPanel = ({ pathwayId, isSubmitted }) => {
  const [purchaseStatus, setPurchaseStatus] = useState(null);  // from /purchase-status
  const [reviewStatus, setReviewStatus] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [applicantNotes, setApplicantNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      // Check purchase status first (Phase 11.1)
      const purchaseData = await apiFetch(`/api/review/${pathwayId}/purchase-status`);
      setPurchaseStatus(purchaseData);

      // If purchased, also get the full review record with comments
      if (purchaseData.has_purchased) {
        const data = await apiFetch(`/api/review/${pathwayId}`);
        setReviewStatus(data.review);
        if (data.review) {
          const commentsData = await apiFetch(`/api/review/${pathwayId}/comments`);
          setComments(commentsData.comments || []);
        }
      }
    } catch (err) {
      // If purchase-status throws 403 (no pathway entitlement), that's fine
      if (err.status !== 403) {
        setError('Failed to load review status');
      }
    } finally {
      setLoading(false);
    }
  }, [pathwayId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handlePurchaseReview = async () => {
    setPurchasing(true);
    setError('');
    try {
      const data = await apiFetch('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({
          pathway_id: pathwayId,
          attorney_review: true,
          attorney_review_only: true,
          aos_addon: false,
        }),
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Failed to start attorney review checkout');
      }
    } catch (err) {
      setError(err.message || 'Failed to start checkout');
    } finally {
      setPurchasing(false);
    }
  };

  const handleActivateReview = async () => {
    setActivating(true);
    setError('');
    setSuccess('');
    try {
      await apiFetch(`/api/review/${pathwayId}/request`, {
        method: 'POST',
        body: JSON.stringify({ notes: applicantNotes }),
      });
      setSuccess('Attorney review activated! An attorney will be assigned shortly.');
      await fetchStatus();
    } catch (err) {
      setError(err.message || 'Failed to activate review');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <i className="fas fa-spinner fa-spin mr-2"></i> Loading review status...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm">{success}</div>
      )}

      {/* ── CASE 1: Not purchased → Upsell with in-app checkout ── */}
      {(!purchaseStatus || !purchaseStatus.has_purchased) && (
        <div>
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-gavel text-yellow-600 text-2xl"></i>
            </div>
            <h4 className="font-bold text-gray-900 text-lg mb-2">Attorney Draft Review <BetaBadge className="ml-2 align-middle" /></h4>
            <p className="text-gray-600 text-sm mb-4 max-w-sm mx-auto">
              Have a licensed immigration attorney review your petition before filing.
              Catch errors, get professional feedback, and increase your chances of approval.
            </p>
            <div className="flex justify-center gap-6 text-sm text-gray-500 mb-5">
              <span><i className="fas fa-check text-green-500 mr-1"></i> Inline comments on your draft</span>
              <span><i className="fas fa-check text-green-500 mr-1"></i> New reviewed document version</span>
            </div>
            {purchaseStatus?.blocked_after_submission || isSubmitted ? (
              <div className="bg-amber-100 border border-amber-300 text-amber-800 rounded-xl p-4 text-sm">
                <i className="fas fa-lock mr-2"></i>
                Attorney review can no longer be purchased — workflow has been submitted.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  {[
                    { tier: 'Simple', price: '$99', desc: 'For family petitions & F-1' },
                    { tier: 'Standard', price: '$149', desc: 'For AOS, O-1, Asylum, PERM' },
                    { tier: 'Complex', price: '$199', desc: 'For EB-2 NIW & EB-1A' },
                  ].map((t) => (
                    <div key={t.tier} className="bg-white border border-yellow-200 rounded-xl p-3 text-center">
                      <div className="font-bold text-yellow-600 text-lg">{t.price}</div>
                      <div className="font-semibold text-gray-800 text-xs">{t.tier}</div>
                      <div className="text-gray-400 text-xs mt-0.5">{t.desc}</div>
                    </div>
                  ))}
                </div>
                {LAUNCH.attorneyReviewPurchases ? (
                <button
                  onClick={handlePurchaseReview}
                  disabled={purchasing}
                  className="w-full bg-yellow-500 text-black py-3 rounded-xl font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-50"
                >
                  {purchasing ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>Starting checkout...</>
                  ) : (
                    <><i className="fas fa-credit-card mr-2"></i>Purchase Attorney Review</>
                  )}
                </button>
                ) : (
                  <PurchaseDisabledButton label="Purchase Attorney Review" />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CASE 2: Purchased but not yet activated ── */}
      {purchaseStatus?.has_purchased && !reviewStatus && (
        <div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2">
              <i className="fas fa-check-circle text-blue-600"></i>
              <p className="text-blue-800 font-semibold text-sm">
                Attorney Review Purchased — {purchaseStatus.review_tier} tier
              </p>
            </div>
            <p className="text-blue-700 text-sm mt-1">
              Click below to activate your review and notify the attorney queue.
            </p>
          </div>
          <textarea
            value={applicantNotes}
            onChange={(e) => setApplicantNotes(e.target.value)}
            placeholder="Optional notes for the reviewing attorney..."
            className="w-full border border-gray-200 rounded-xl p-3 text-sm mb-3"
            rows={3}
          />
          <button
            onClick={handleActivateReview}
            disabled={activating}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-500 disabled:opacity-50"
          >
            {activating ? 'Activating...' : 'Activate Attorney Review'}
          </button>
        </div>
      )}

      {/* ── CASE 3: Review exists (active / in_review / completed) ── */}
      {reviewStatus && (
        <div>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${REVIEW_STATUS_COLORS[reviewStatus.status] || 'text-gray-600 bg-gray-50'}`}>
            {REVIEW_STATUS_LABELS[reviewStatus.status] || reviewStatus.status}
          </div>

          <div className="mt-4 text-sm text-gray-600 space-y-1">
            <p><span className="font-medium">Tier:</span> {reviewStatus.review_tier_label}</p>
            {reviewStatus.attorney_id && (
              <p><span className="font-medium">Attorney assigned:</span> ✓</p>
            )}
            {reviewStatus.completed_at && (
              <p><span className="font-medium">Completed:</span> {new Date(reviewStatus.completed_at).toLocaleDateString()}</p>
            )}
            {reviewStatus.second_doc_generated && (
              <p className="text-green-700">
                <i className="fas fa-file-alt mr-1"></i>
                Reviewed document version generated — check Documents tab
              </p>
            )}
          </div>

          {/* Overall Feedback */}
          {reviewStatus.overall_feedback && (
            <div className={`mt-4 p-4 rounded-lg border ${reviewStatus.approved ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{reviewStatus.approved ? '✅' : '⚠️'}</span>
                <span className="font-semibold text-gray-800">
                  {reviewStatus.approved ? 'Looks Good!' : 'Needs Revision'}
                </span>
              </div>
              <p className="text-gray-700 text-sm">{reviewStatus.overall_feedback}</p>
            </div>
          )}

          {/* Attorney Comments */}
          {comments.length > 0 && (
            <div className="mt-5">
              <h4 className="font-semibold text-gray-800 mb-3">
                <i className="fas fa-comments mr-2 text-purple-500"></i>
                Attorney Comments ({comments.length})
              </h4>
              <div className="space-y-3">
                {comments.map((comment) => (
                  <div key={comment.id} className={`rounded-lg p-4 border ${comment.orphaned ? 'border-amber-200 bg-amber-50' : 'border-purple-200 bg-purple-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-purple-700 uppercase">
                        Step: {comment.workflow_step_id}
                        {comment.field_id && ` → ${comment.field_id}`}
                        {comment.step_name_at_creation && ` (${comment.step_name_at_creation})`}
                      </span>
                      <span className="text-xs text-gray-400">
                        {comment.timestamp ? new Date(comment.timestamp).toLocaleDateString() : ''}
                        {comment.edited && ' (edited)'}
                        {comment.orphaned && <span className="ml-2 text-amber-600">⚠️ Orphaned</span>}
                      </span>
                    </div>
                    <p className="text-gray-800 text-sm">{comment.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waiting for attorney note */}
          {reviewStatus.status === 'active' && !reviewStatus.attorney_id && (
            <div className="mt-4 text-center py-4 text-gray-500 text-sm">
              <i className="fas fa-clock mr-2 text-blue-400"></i>
              Waiting for an attorney to be assigned…
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Document Panel
// ---------------------------------------------------------------------------
const DocumentPanel = ({ pathwayId, workflowStatus, initialTab = 'documents', onClose }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab || 'documents');

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);
  const [showFormExportModal, setShowFormExportModal] = useState(false);  // Phase 6
  const [showCaseStatusModal, setShowCaseStatusModal] = useState(false);  // Phase 7

  const isSubmitted = workflowStatus === 'submitted';

  const fetchDocuments = useCallback(async () => {
    if (!isSubmitted) {
      setLoading(false);
      return;
    }
    try {
      const data = await apiFetch(`/api/documents/${pathwayId}`);
      setDocs(data.documents || []);
    } catch {
      // Docs may not exist yet
    } finally {
      setLoading(false);
    }
  }, [pathwayId, isSubmitted]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleViewDocument = async (docMeta) => {
    try {
      const fullDoc = await apiFetch(`/api/documents/${pathwayId}/${docMeta.id}`);
      setViewingDoc(fullDoc);
    } catch (err) {
      setError('Failed to load document content.');
    }
  };

  const handleGenerateDocs = async () => {
    setGenerating(true);
    setError('');
    try {
      const data = await apiFetch(`/api/documents/${pathwayId}/generate`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setDocs(data.documents || []);
    } catch (err) {
      setError(err.message || 'Failed to generate documents.');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateDoc = async (docType) => {
    setError('');
    try {
      await apiFetch(`/api/documents/${pathwayId}/generate`, {
        method: 'POST',
        body: JSON.stringify({ doc_type: docType }),
      });
      await fetchDocuments();
    } catch (err) {
      setError(err.message || 'Failed to regenerate document.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              <i className="fas fa-file-export mr-2 text-yellow-500"></i>
              Documents & Attorney Review
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isSubmitted
                ? 'Your petition documents and review options'
                : 'Submit your workflow to generate documents'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {[
            { id: 'documents', label: 'Documents', icon: 'fa-file-alt' },
            { id: 'review', label: 'Attorney Review', icon: 'fa-gavel' },
            { id: 'form_export', label: 'Form Export', icon: 'fa-file-pdf' },
            { id: 'case_status', label: 'Case Status', icon: 'fa-chart-line' },
            { id: 'evidence_builder', label: 'Evidence Builder', icon: 'fa-folder-open' },
            ...(EB_PATHWAYS.has(pathwayId)
              ? [{ id: 'priority_date', label: 'Priority Date', icon: 'fa-calendar-alt' }]
              : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center gap-2 transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`fas ${tab.icon}`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              {!isSubmitted ? (
                <div className="text-center py-10 text-gray-500">
                  <i className="fas fa-lock text-4xl mb-4 block text-gray-300"></i>
                  <p className="font-medium text-gray-700">Workflow not submitted yet</p>
                  <p className="text-sm mt-2">Complete and submit your workflow to generate petition documents.</p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-10 text-gray-400">
                  <i className="fas fa-spinner fa-spin mr-2"></i> Loading documents...
                </div>
              ) : docs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-file-alt text-4xl mb-4 block text-gray-300"></i>
                  <p className="font-medium text-gray-700 mb-4">No documents generated yet</p>
                  <button
                    onClick={handleGenerateDocs}
                    disabled={generating}
                    className="px-6 py-3 bg-yellow-500 text-white font-semibold rounded-xl hover:bg-yellow-600 transition-colors disabled:opacity-50"
                  >
                    {generating ? (
                      <><i className="fas fa-spinner fa-spin mr-2"></i>Generating...</>
                    ) : (
                      <><i className="fas fa-magic mr-2"></i>Generate Documents</>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-600 font-medium">
                      {docs.length} document{docs.length !== 1 ? 's' : ''} ready
                    </span>
                    <button
                      onClick={handleGenerateDocs}
                      disabled={generating}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <i className="fas fa-sync-alt"></i>
                      {generating ? 'Regenerating...' : 'Regenerate All'}
                    </button>
                  </div>

                  {docs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-yellow-300 hover:bg-yellow-50/30 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{DOC_ICONS[doc.document_type] || '📄'}</span>
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">
                            {doc.document_type_label}
                            {doc.document_type === 'narrative' && (
                              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${
                                doc.narrative_status === 'finalized'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {doc.narrative_status === 'finalized' ? 'Final' : 'Draft'}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-400">
                            v{doc.version} ·{' '}
                            {doc.generated_at ? new Date(doc.generated_at).toLocaleDateString() : '—'}
                            {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(1)} KB` : ''}
                            {doc.generation_trigger === 'attorney_review' ? ' · After review' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(doc.document_type === 'petition_pdf' || doc.has_binary) && (
                          <button
                            onClick={async () => {
                              try {
                                await downloadBinary(
                                  `/api/documents/${pathwayId}/${doc.id}/download/pdf`,
                                  `${doc.document_type}_${pathwayId}.pdf`
                                );
                              } catch (err) {
                                setError(err.message || 'PDF download failed');
                              }
                            }}
                            className="text-xs px-2 py-1 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                            title="Download PDF"
                          >
                            <i className="fas fa-file-pdf"></i>
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            try {
                              await downloadBinary(
                                `/api/documents/${pathwayId}/${doc.id}/download/docx`,
                                `${doc.document_type}_${pathwayId}.docx`
                              );
                            } catch (err) {
                              setError(err.message || 'DOCX download failed');
                            }
                          }}
                          className="text-xs px-2 py-1 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Download DOCX"
                        >
                          <i className="fas fa-file-word"></i>
                        </button>
                        <button
                          onClick={() => handleRegenerateDoc(doc.document_type)}
                          className="text-xs px-2 py-1 text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          title="Regenerate this document"
                        >
                          <i className="fas fa-redo"></i>
                        </button>
                        <button
                          onClick={() => handleViewDocument(doc)}
                          className="text-xs px-3 py-1.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-semibold"
                        >
                          <i className="fas fa-eye mr-1"></i>
                          {doc.document_type === 'narrative' ? 'Edit' : 'View'}
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                    <i className="fas fa-exclamation-triangle mr-1"></i>
                    These are draft documents. Review with a qualified attorney before filing with USCIS.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Attorney Review Tab */}
          {activeTab === 'review' && (
            <AttorneyReviewPanel pathwayId={pathwayId} isSubmitted={isSubmitted} />
          )}

          {/* Form Export Tab (Phase 6) */}
          {activeTab === 'form_export' && (
            <div>
              {isSubmitted ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-file-pdf text-red-600 text-2xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">USCIS Form Pre-Population</h3>
                  <p className="text-gray-500 text-sm mb-5 max-w-sm mx-auto">
                    Your workflow data is ready to be mapped to the official USCIS form fields
                    for export as a PDF or plain-text reference document.
                  </p>
                  <button
                    onClick={() => setShowFormExportModal(true)}
                    className="px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors shadow-sm"
                  >
                    <i className="fas fa-file-pdf mr-2"></i>Open Form Export
                  </button>
                  <p className="text-xs text-gray-400 mt-3">
                    View pre-populated fields, check completeness, and download PDF or text.
                  </p>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  <i className="fas fa-lock text-4xl mb-4 block text-gray-300"></i>
                  <p className="font-medium text-gray-700">Workflow not submitted yet</p>
                  <p className="text-sm mt-2">Submit your workflow to unlock form pre-population.</p>
                </div>
              )}
            </div>
          )}
          {/* Case Status Tab (Phase 7) */}
          {activeTab === 'case_status' && (
            <div>
              {isSubmitted ? (
                <div className="text-center py-6">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: '#FEF3C7' }}
                  >
                    <i className="fas fa-chart-line text-2xl" style={{ color: '#C6A664' }}></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">Case Status Tracker</h3>
                  <p className="text-gray-500 text-sm mb-5 max-w-sm mx-auto">
                    Track your USCIS receipt number, record status updates, view your case
                    history, and add personal notes — all in one place.
                  </p>
                  <button
                    onClick={() => setShowCaseStatusModal(true)}
                    className="px-6 py-3 font-semibold rounded-xl transition-colors shadow-sm"
                    style={{ backgroundColor: '#C6A664', color: '#1B1B1E' }}
                  >
                    <i className="fas fa-chart-line mr-2"></i>Open Case Status Tracker
                  </button>
                  <p className="text-xs text-gray-400 mt-3">
                    Enter your USCIS receipt number, update status, and view your processing timeline.
                  </p>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  <i className="fas fa-lock text-4xl mb-4 block text-gray-300"></i>
                  <p className="font-medium text-gray-700">Workflow not submitted yet</p>
                  <p className="text-sm mt-2">Submit your workflow to enable case status tracking.</p>
                </div>
              )}
            </div>
          )}

          {/* Evidence Builder Tab (Phase 8) */}
          {activeTab === 'evidence_builder' && (
            <EvidenceBuilderPanel
              pathwayId={pathwayId}
              workflowStatus={workflowStatus}
            />
          )}

          {/* Priority Date Intelligence Tab (Phase 9) */}
          {activeTab === 'priority_date' && EB_PATHWAYS.has(pathwayId) && (
            <PriorityDatePanel pathwayId={pathwayId} />
          )}
        </div>
      </div>

      {/* Phase 6: Form Export Modal (launched from Form Export tab) */}
      {showFormExportModal && (
        <FormExportPanel
          pathwayId={pathwayId}
          workflowStatus={workflowStatus}
          onClose={() => setShowFormExportModal(false)}
        />
      )}

      {/* Phase 7: Case Status Modal (launched from Case Status tab) */}
      {showCaseStatusModal && (
        <CaseStatusPanel
          pathwayId={pathwayId}
          onClose={() => setShowCaseStatusModal(false)}
        />
      )}

      {/* Document Viewer Modal */}
      {viewingDoc && (
        <DocumentViewer
          key={`${viewingDoc.id}-${viewingDoc.narrative_status || 'open'}-${viewingDoc.content?.length || 0}`}
          doc={viewingDoc}
          pathwayId={pathwayId}
          onClose={() => setViewingDoc(null)}
          onNarrativeUpdated={(updated) => {
            setViewingDoc(updated);
            fetchDocuments();
          }}
        />
      )}
    </div>
  );
};

export default DocumentPanel;
